import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Grid3x3, Search, ShoppingCart, User, Menu, X, MapPin, Phone, Mail, Clock, Facebook, Instagram, Youtube, Settings, Camera, ChevronRight, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Toaster } from "sonner";
import MaintenancePage from "@/pages/MaintenancePage";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.1a8.16 8.16 0 0 0 4.77 1.53V6.19a4.85 4.85 0 0 1-1.84-.5Z" />
  </svg>
);

const NavLink = ({ to, icon: Icon, label, active, count }) => (
  <Link to={to} data-testid={`bottom-nav-${label.toLowerCase()}`}
    className={`flex flex-col items-center justify-center gap-1 flex-1 relative min-h-[56px] transition-colors ${active ? "text-[#FFD600]" : "text-neutral-400"}`}>
    <div className="relative">
      <Icon size={22} strokeWidth={2} />
      {count > 0 && (
        <span className="absolute -top-2 -right-3 bg-[#FFD600] text-black text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
          {count}
        </span>
      )}
    </div>
    <span className="text-[10px] font-medium uppercase tracking-wider">{label}</span>
  </Link>
);

export default function Layout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { cartCount, user } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const [s, setS] = useState({});

  useEffect(() => {
    api.get("/settings").then(r => setS(r.data || {})).catch(() => {});
  }, []);

  const isActive = (p) => loc.pathname === p || (p !== "/" && loc.pathname.startsWith(p));

  const isAdmin = user?.role === "admin";
  const isAdminRoute = loc.pathname.startsWith("/admin") || loc.pathname === "/login" || loc.pathname === "/skano";
  const showMaintenance = s.maintenance_mode && !isAdmin && !isAdminRoute;

  if (showMaintenance) {
    return (
      <div className="min-h-screen bg-[#0B1220] text-white">
        <MaintenancePage message={s.maintenance_message} />
        <Toaster position="top-center" theme="dark" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1220] text-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0B1220]/95 backdrop-blur-md border-b border-[#1F2B44] print:hidden">
        <div className="max-w-7xl mx-auto px-3 h-20 flex items-center justify-between gap-2">
          <button data-testid="menu-btn" onClick={() => setMenuOpen(true)} className="md:hidden p-2 -ml-2">
            <Menu size={24} />
          </button>
          <Link to="/" data-testid="logo-link" className="flex-1 flex items-center justify-center md:justify-start md:flex-none">
            <img src="/logo-hidrogv.png"
              alt="HidroGV" className="h-14 sm:h-16 md:h-20 w-auto object-contain max-w-full" />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm uppercase tracking-wide">
            <Link to="/" data-testid="nav-home" className={isActive("/") && loc.pathname === "/" ? "text-[#FFD600]" : "hover:text-[#FFD600]"}>Ballina</Link>
            <Link to="/kategori" data-testid="nav-categories" className={isActive("/kategori") ? "text-[#FFD600]" : "hover:text-[#FFD600]"}>Kategori</Link>
            <Link to="/konfigurator" data-testid="nav-configurator" className={isActive("/konfigurator") ? "text-[#FFD600]" : "hover:text-[#FFD600]"}>Konfigurator</Link>
            <Link to="/kerko-me-foto" data-testid="nav-photo" className={isActive("/kerko-me-foto") ? "text-[#FFD600]" : "hover:text-[#FFD600]"}>Kërko me foto</Link>
            <Link to="/kontakt" data-testid="nav-kontakt" className={isActive("/kontakt") ? "text-[#FFD600]" : "hover:text-[#FFD600]"}>Kontakt</Link>
          </nav>
          <div className="flex items-center gap-1">
            <button data-testid="search-header-btn" onClick={() => nav("/kerko")} className="p-2 hover:text-[#FFD600]"><Search size={20} /></button>
            <Link to="/shporta" data-testid="cart-header-btn" className="relative p-2 hover:text-[#FFD600]">
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span className="absolute top-0 right-0 bg-[#FFD600] text-black text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center">{cartCount}</span>
              )}
            </Link>
            <Link to={user ? (user.role === "admin" ? "/admin" : "/profili") : "/login"} data-testid="account-header-btn" className="p-2 hover:text-[#FFD600]"><User size={20} /></Link>
          </div>
        </div>
      </header>

      {/* Mobile side menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-[#0B1220]/70" />
          <div className="absolute left-0 top-0 h-full w-[85%] max-w-[360px] bg-[#0B1220] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            {/* Header: Logo + X */}
            <div className="flex justify-between items-start px-5 pt-5 pb-4">
              <Link to="/" onClick={() => setMenuOpen(false)} className="flex-1">
                <img src="/logo-hidrogv.png" alt="HidroGV" className="h-20 w-auto object-contain" />
              </Link>
              <button data-testid="close-menu-btn" onClick={() => setMenuOpen(false)}
                className="p-2 -mt-1 text-white hover:text-[#FFD600] transition-colors">
                <X size={26} strokeWidth={2.2} />
              </button>
            </div>

            {/* Menu items */}
            <nav className="flex-1 overflow-y-auto px-5">
              {[
                { to: "/kontakt", label: "Kontakt", icon: Phone },
                { to: "/", label: "Ballina", icon: Home },
                { to: "/kategori", label: "Kategori", icon: Grid3x3 },
                { to: "/konfigurator", label: "Konfiguro Tubin", icon: Settings },
                { to: "/kerko-me-foto", label: "Kërko me foto", icon: Camera },
                { to: "/kerko", label: "Kërko", icon: Search },
                { to: "/shporta", label: "Shporta", icon: ShoppingCart },
                { to: user ? (user.role === "admin" ? "/admin" : "/profili") : "/login", label: "Llogaria", icon: User },
              ].map((it, i, arr) => (
                <Link key={it.label} to={it.to} onClick={() => setMenuOpen(false)}
                  data-testid={`menu-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-4 py-4 group ${i < arr.length - 1 ? "border-b border-[#1e1e1e]" : ""}`}>
                  <span className="w-10 flex items-center justify-center text-[#FFD600]">
                    <it.icon size={26} strokeWidth={1.8} />
                  </span>
                  <span className="flex-1 text-white font-bold tracking-wider text-[15px] uppercase group-hover:text-[#FFD600] transition-colors">
                    {it.label}
                  </span>
                  <ChevronRight size={20} className="text-[#FFD600]" strokeWidth={2.2} />
                </Link>
              ))}
            </nav>

            {/* Socials */}
            <div className="px-5 pb-8 pt-4">
              <div className="flex items-center justify-between gap-3">
                {[
                  { href: s.facebook_url, icon: Facebook, label: "Facebook" },
                  { href: s.instagram_url, icon: Instagram, label: "Instagram" },
                  { href: s.whatsapp_number ? `https://wa.me/${(s.whatsapp_number || "").replace(/\D/g, "")}` : "", icon: MessageCircle, label: "WhatsApp" },
                  { href: s.tiktok_url, icon: null, label: "TikTok", custom: TikTokIcon },
                  { href: s.youtube_url, icon: Youtube, label: "YouTube" },
                ].map((soc) => {
                  const disabled = !soc.href;
                  const Inner = (
                    <span className={`w-11 h-11 flex items-center justify-center border-2 rounded-full ${disabled ? "border-[#3a3300] text-[#7a6c00]" : "border-[#FFD600] text-[#FFD600] hover:bg-[#FFD600] hover:text-black"} transition-colors`}>
                      {soc.custom ? <soc.custom size={20} /> : <soc.icon size={20} strokeWidth={2} />}
                    </span>
                  );
                  return disabled ? (
                    <span key={soc.label} data-testid={`menu-soc-${soc.label.toLowerCase()}`} title={soc.label} className="pointer-events-none">{Inner}</span>
                  ) : (
                    <a key={soc.label} href={soc.href} target="_blank" rel="noreferrer"
                      data-testid={`menu-soc-${soc.label.toLowerCase()}`} title={soc.label}>
                      {Inner}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 pb-20 md:pb-0">{children}</main>

      {/* Footer desktop */}
      <footer className="hidden md:block border-t border-[#1F2B44] bg-[#0B1220] py-10 mt-10 print:hidden">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-4 gap-8 text-sm">
          <div>
            <img src="/logo-hidrogv.png"
              alt="HidroGV" className="h-16 w-auto object-contain mb-3" />
            <p className="text-neutral-400">Zgjidhja juaj për hidraulikë në Kosovë.</p>
            {(s.facebook_url || s.instagram_url || s.tiktok_url || s.youtube_url) && (
              <div className="flex gap-2 mt-4">
                {s.facebook_url && <a href={s.facebook_url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded bg-[#1a1a1a] hover:bg-[#FFD600] hover:text-black flex items-center justify-center transition-colors" data-testid="footer-fb"><Facebook size={16} /></a>}
                {s.instagram_url && <a href={s.instagram_url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded bg-[#1a1a1a] hover:bg-[#FFD600] hover:text-black flex items-center justify-center transition-colors" data-testid="footer-ig"><Instagram size={16} /></a>}
                {s.tiktok_url && <a href={s.tiktok_url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded bg-[#1a1a1a] hover:bg-[#FFD600] hover:text-black flex items-center justify-center transition-colors" data-testid="footer-tt"><TikTokIcon size={16} /></a>}
                {s.youtube_url && <a href={s.youtube_url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded bg-[#1a1a1a] hover:bg-[#FFD600] hover:text-black flex items-center justify-center transition-colors" data-testid="footer-yt"><Youtube size={16} /></a>}
              </div>
            )}
          </div>
          <div>
            <div className="font-semibold uppercase text-xs tracking-widest mb-3 text-[#FFD600]">Kategori</div>
            <ul className="space-y-2 text-neutral-400">
              <li><Link to="/kategori">Të gjitha</Link></li>
              <li><Link to="/kategori/tuba-hidraulike">Tuba</Link></li>
              <li><Link to="/kategori/koplinge-hidraulike">Koplingë</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold uppercase text-xs tracking-widest mb-3 text-[#FFD600]">Informacione</div>
            <ul className="space-y-2 text-neutral-400">
              <li><Link to="/konfigurator" className="hover:text-[#FFD600]">Konfiguro tubin</Link></li>
              <li><Link to="/kerko-me-foto" className="hover:text-[#FFD600]">Kërko me foto</Link></li>
              <li><Link to="/kontakt" className="hover:text-[#FFD600]">Kontakti</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold uppercase text-xs tracking-widest mb-3 text-[#FFD600]">Kontakt</div>
            <ul className="space-y-2 text-neutral-400">
              {s.address && <li className="flex gap-2"><MapPin size={14} className="text-[#FFD600] mt-0.5 flex-shrink-0" /><span className="whitespace-pre-line">{s.address}</span></li>}
              {s.contact_phone && <li className="flex gap-2"><Phone size={14} className="text-[#FFD600] mt-0.5 flex-shrink-0" /><a href={`tel:${s.contact_phone}`} className="hover:text-[#FFD600]">{s.contact_phone}</a></li>}
              {s.email && <li className="flex gap-2"><Mail size={14} className="text-[#FFD600] mt-0.5 flex-shrink-0" /><a href={`mailto:${s.email}`} className="hover:text-[#FFD600] break-all">{s.email}</a></li>}
              {s.working_hours && <li className="flex gap-2"><Clock size={14} className="text-[#FFD600] mt-0.5 flex-shrink-0" /><span className="whitespace-pre-line">{s.working_hours}</span></li>}
              {!s.address && !s.contact_phone && !s.email && !s.working_hours && <li>Kosovë</li>}
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-8 pt-6 border-t border-[#1F2B44] text-xs text-neutral-500">
          © {new Date().getFullYear()} HidroGV. Të gjitha të drejtat e rezervuara.
        </div>
      </footer>

      {/* Bottom nav mobile */}
      <nav className="fixed bottom-0 left-0 right-0 h-16 bg-[#0B1220] border-t border-[#1F2B44] flex justify-around items-center z-40 md:hidden print:hidden">
        <NavLink to="/" icon={Home} label="Ballina" active={loc.pathname === "/"} />
        <NavLink to="/kategori" icon={Grid3x3} label="Kategori" active={isActive("/kategori")} />
        <NavLink to="/kerko" icon={Search} label="Kërko" active={isActive("/kerko")} />
        <NavLink to="/shporta" icon={ShoppingCart} label="Shporta" active={isActive("/shporta")} count={cartCount} />
        <NavLink to={user ? (user.role === "admin" ? "/admin" : "/profili") : "/login"} icon={User} label="Llogaria" active={isActive("/login") || isActive("/admin") || isActive("/profili")} />
      </nav>

      <Toaster position="top-center" theme="dark" />
    </div>
  );
}
